import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { API_IMAGE_BASE_URL } from "../../config/api";
import "./PortfolioApproval.css";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const PortfolioApproval = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rejectTarget, setRejectTarget] = useState(null); // item being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [preview, setPreview] = useState(null); // item being viewed full-size
  const limit = 20;

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/portfolio-media", {
        params: { status: status || undefined, search: search || undefined, page, limit },
      });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch portfolio media:", err);
      toast.error("Failed to load portfolio media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchItems();
  };

  const updateStatus = async (item, newStatus, reason = null) => {
    try {
      await api.patch("/admin/users/media-status", {
        vendorId: item.vendorId,
        mediaId: item.mediaId,
        mediaType: item.mediaType,
        status: newStatus,
        reason,
      });
      toast.success(`Media ${newStatus}`);
      setItems((prev) => prev.filter((i) => i.mediaId !== item.mediaId));
    } catch (err) {
      console.error("Failed to update media status:", err);
      toast.error(err.response?.data?.message || "Failed to update media status");
    }
  };

  const handleApprove = (item) => updateStatus(item, "approved");

  const openRejectModal = (item) => {
    setRejectTarget(item);
    setRejectReason("");
  };

  const submitReject = () => {
    if (!rejectTarget) return;
    updateStatus(rejectTarget, "rejected", rejectReason || "Rejected by admin");
    setRejectTarget(null);
  };

  return (
    <div className="portfolio-approval-page">
      <div className="page-header">
        <h2>Portfolio Approval</h2>
      </div>

      <div className="filters-bar">
        <div className="status-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`status-tab ${status === tab.key ? "active" : ""}`}
              onClick={() => {
                setStatus(tab.key);
                setPage(1);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form className="search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search vendor name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="empty-state">No portfolio media found for this filter.</div>
      ) : (
        <div className="portfolio-review-grid">
          {items.map((item) => (
            <div className="review-card" key={`${item.vendorId}-${item.mediaId}`}>
              <div className="review-thumb" onClick={() => setPreview(item)}>
                {item.mediaType === "videos" ? (
                  <video src={`${API_IMAGE_BASE_URL}${item.url}`} />
                ) : (
                  <img src={`${API_IMAGE_BASE_URL}${item.url}`} alt={item.vendorName} />
                )}
                <span className={`status-badge status-${item.status}`}>{item.status}</span>
              </div>
              <div className="review-meta">
                <p className="vendor-name">{item.vendorName}</p>
                {item.businessName && <p className="business-name">{item.businessName}</p>}
                <p className="uploaded-at">
                  {item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString("en-GB") : "—"}
                </p>
                {item.status === "rejected" && item.rejectionReason && (
                  <p className="rejection-reason">Reason: {item.rejectionReason}</p>
                )}
              </div>
              <div className="review-actions">
                <button className="btn-approve" onClick={() => handleApprove(item)}>
                  Approve
                </button>
                <button className="btn-reject" onClick={() => openRejectModal(item)}>
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {rejectTarget && (
        <div className="modal-overlay" onClick={() => setRejectTarget(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Media</h3>
            <textarea
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button onClick={() => setRejectTarget(null)}>Cancel</button>
              <button className="btn-reject" onClick={submitReject}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal-box modal-preview" onClick={(e) => e.stopPropagation()}>
            {preview.mediaType === "videos" ? (
              <video src={`${API_IMAGE_BASE_URL}${preview.url}`} controls autoPlay />
            ) : (
              <img src={`${API_IMAGE_BASE_URL}${preview.url}`} alt={preview.vendorName} />
            )}
            <button className="modal-close" onClick={() => setPreview(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioApproval;
